import { useState } from "react";
import { SearchNormal1 } from "iconsax-react";
import { EmptyState, Pagination } from "@/shared/components";
import { Input, Prompt, PromptForm, Skeleton } from "@/shared/ui";
import { useAuth } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts/permissions";
import {
  VolunteersHeader,
  VolunteersTableView,
  VolunteerCreateModal,
  VolunteerAssignProjectsModal,
} from "../../components";
import { useVolunteersListPage } from "../../hooks/useVolunteersListPage";
import { useVolunteerCreationFlow } from "../../hooks/useVolunteerCreationFlow";
import type { Volunteer } from "../../types";

interface PromptState {
  title: string;
  text: string;
  variant: "success" | "error";
}

export default function VolunteersPage() {
  const {
    volunteers,
    totalCount,
    search,
    setSearch,
    sortConfig,
    handleSort,
    pagination,
    isLoading,
    isError,
    locale,
    t,
  } = useVolunteersListPage();

  const { hasPermission } = useAuth();

  const {
    createdVolunteerId,
    showAssignPrompt,
    showAssignModal,
    onVolunteerCreated,
    onAssignConfirm,
    onAssignSkip,
    onAssignComplete,
  } = useVolunteerCreationFlow();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [prompt, setPrompt] = useState<PromptState | null>(null);
  const [, setSelectedVolunteer] = useState<Volunteer | null>(null);

  function handleVolunteerCreated(volunteerId: number) {
    onVolunteerCreated(volunteerId);
  }

  function handleAssignComplete() {
    onAssignComplete();
    setPrompt({
      title: t("prompt.assigned.title"),
      text: t("prompt.assigned.text"),
      variant: "success",
    });
  }

  if (isLoading) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1 min-w-0">
          <VolunteersHeader onAddClick={() => setShowCreateModal(true)} />
          <VolunteersPageSkeleton />
        </section>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1 min-w-0">
          <VolunteersHeader onAddClick={() => setShowCreateModal(true)} />
          <p className="mt-4 text-body-regular-md content-action-negative">{t("messages.error")}</p>
        </section>
      </>
    );
  }

  return (
    <>
      <title>{t("meta.title")}</title>

      {prompt && !showAssignPrompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          onClose={() => setPrompt(null)}
          namespace="VolunteersPage"
        />
      )}

      {showCreateModal && hasPermission(PERMISSIONS.PROJECTS.CREATE_VOLUNTEER) && (
        <VolunteerCreateModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleVolunteerCreated}
        />
      )}

      {showAssignPrompt && (
        <PromptForm
          title={t("prompt.created.title")}
          text={t("prompt.created.assignQuestion")}
          variant="success"
          onClose={onAssignSkip}
          onConfirm={onAssignConfirm}
          confirmText={t("prompt.created.assignConfirm")}
          cancelText={t("prompt.created.assignSkip")}
          namespace="VolunteersPage"
        />
      )}

      {showAssignModal && createdVolunteerId !== null && (
        <VolunteerAssignProjectsModal
          volunteerId={createdVolunteerId}
          onClose={onAssignComplete}
          onAssigned={handleAssignComplete}
        />
      )}

      <section className="flex-1 min-w-0">
        <VolunteersHeader onAddClick={() => setShowCreateModal(true)} />

        <div className="radius-3xl surface-base-fill p-7">
          <div className="flex items-center gap-2 mb-7">
            <div className="min-w-48">
              <Input
                placeholder={t("search.placeholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                icon={<SearchNormal1 size={14} color="currentColor" />}
                isSmall
              />
            </div>
          </div>

          {volunteers.length === 0 ? (
            <EmptyState
              hasActiveFilters={search.length > 0}
              noResultsText={t("table.noResults")}
              noDataText={t("table.noData")}
              noDataHint={t("table.noDataHint")}
            />
          ) : (
            <>
              <VolunteersTableView
                volunteers={volunteers}
                locale={locale}
                sortConfig={sortConfig}
                onSort={handleSort}
                onSelectVolunteer={setSelectedVolunteer}
              />

              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={pagination.setPage}
                fromItem={pagination.fromItem}
                toItem={pagination.toItem}
                total={totalCount}
              />
            </>
          )}
        </div>
      </section>
    </>
  );
}

function VolunteersPageSkeleton() {
  return (
    <div className="radius-3xl surface-base-fill p-7">
      <div className="flex items-center justify-between gap-2 mb-7">
        <div className="flex items-center gap-2">
          <Skeleton width={192} height={32} className="radius-xs" />
        </div>
        <div className="flex items-center gap-3">
          <Skeleton width={180} height={36} className="radius-xs" />
        </div>
      </div>

      <div className="flex flex-col">
        <div className="grid grid-cols-6 gap-4 pb-3 border-b surface-base-stroke">
          {Array.from({ length: 6 }).map(function renderHeadSkeleton(_, i) {
            return <Skeleton key={i} height={20} />;
          })}
        </div>
        {Array.from({ length: 6 }).map(function renderRowSkeleton(_, rowIndex) {
          return (
            <div key={rowIndex} className="grid grid-cols-6 gap-4 py-3 border-b surface-base-stroke">
              {Array.from({ length: 6 }).map(function renderCellSkeleton(_, colIndex) {
                return <Skeleton key={colIndex} height={20} />;
              })}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-4">
        <Skeleton width={200} height={20} />
        <div className="flex items-center gap-2">
          <Skeleton width={32} height={32} />
          <Skeleton width={32} height={32} />
          <Skeleton width={32} height={32} />
        </div>
      </div>
    </div>
  );
}
